/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { formAmendForm, errorMessages, multiSelectControls, choiceFields } from '../../../shared/form-lib';
import { logErrorToCloudReporting, fplsFetch } from '../../../shared/utils';

export const fetchForm = createAsyncThunk('share/fetchForm', async (formId) => {
  try {
    return await fplsFetch({ url: `/api/v2/user/form/${formId}` });
  } catch (error) {
    console.error('An error occurred while fetching share page form: ', error);
  }
});

const allowedEmailInvitePrefillCtrls = [
  'prepended_text',
  'appended_text',
  'labeled_input',
  'number_input',
  'text_input',
  'text_area',
  'website',
  'email'
];

const emailInviteHistoryFilters = {
  time_submitted: { startDate: '', endDate: '' },
  frequency: undefined,
  submitted: '',
  status: '',
  email: ''
};

export const initialMapping = {
  column: undefined,
  columnText: '',
  pageText: '',
  label: '',
  value: ''
};

const allowedPrefillCtrls = [
  ...allowedEmailInvitePrefillCtrls,
  'select_multiple',
  'radio_buttons',
  'select_basic',
  'short_text',
  'checkboxes',
  'country',
  'phone'
];

const emailInvite = {
  subject: 'You have been invited to fill a form',
  filters: emailInviteHistoryFilters,
  isSendingInviteReminder: false,
  isFetchingInviteHistory: false,
  isFetchingEmailInvite: false,
  isCreatingInvitesList: false,
  isSavingInviteMessage: false,
  isSendingEmailInvite: false,
  isSendingTestInvite: false,
  isSavingPrefillData: false,
  allHistoryRetrieved: false,
  contactGroupId: undefined,
  isSavingForLater: false,
  uniqueSubmission: false,
  uploadedCSVFileName: '',
  isDataRetrieved: false,
  contactMethod: 'paste',
  savedForLater: false,
  sendTestEmail: false,
  prevContactList: [],
  prefillControls: [],
  showPrefill: false,
  contactDataMap: [],
  prevCursor: null,
  nextCursor: null,
  replyToEmail: '',
  inviteOption: '',
  contactList: [],
  senderName: '',
  inviteQuota: 0,
  testEmail: '',
  direction: '',
  activeTab: 0,
  mappings: [],
  more: false,
  headers: [],
  message: '',
  columns: [],
  history: [],
  maxQuota: 0,
  groups: []
};

const getPrefillCtrls = (pages) => {
  const emailInvitePrefillCtrls = [];
  const prefillCtrls = [];

  pages.forEach((page, pageIdx) => {
    page.controls.forEach(({ internal_id, display_id, props, type }) => {
      if (allowedEmailInvitePrefillCtrls.includes(type)) {
        emailInvitePrefillCtrls.push({
          pageText: `(Page ${pageIdx + 1})`,
          label: props.label,
          disabled: false,
          internal_id
        });
      }

      if (allowedPrefillCtrls.includes(type)) {
        const inputType = choiceFields.includes(type) ? 'select' : type === 'country' ? 'country' : 'text';
        const isMultiSelectCtrl = multiSelectControls.includes(type);
        const placeholders = {
          select: `Select option${isMultiSelectCtrl ? '(s)' : ''} to be prefilled`,
          text: 'Input data to be prefilled',
        };

        prefillCtrls.push({
          description: isMultiSelectCtrl ? 'Select the option(s) to be prefilled. Long press your Ctrl/Cmd key to select more than one option.' : '',
          options: props.options?.map((option) => ({ value: option, text: option })),
          label: `${props.label} (Page ${pageIdx + 1})`,
          placeholder: placeholders[inputType],
          value: isMultiSelectCtrl ? [] : '',
          prefillValue: undefined,
          displayId: display_id,
          isMultiSelectCtrl,
          inputType
        });
      }
    });
  });

  return { emailInvitePrefillCtrls, prefillCtrls };
};

export const defaultState = {
  activeSidebarTab: 'quick-share',
  showMobileSidebar: false,
  reviewScale: undefined,
  generatedCustomUrl: '',
  userCustomTexts: null,
  isFetchingForm: true,
  isSavingForm: false,
  prefillControls: [],
  prefilledUrl: '',
  activeModal: '',
  shareForm: null,
  customUrl: '',
  shortUrl: '',
  emailInvite,
  formUrl: '',
  error: null,
  form: null,
  userOptions: {
    notification_emails: [],
    verified: false,
    subdomain: ''
  }
};

const shareSlice = createSlice({
  initialState: defaultState,
  name: 'share',
  reducers: {
    clearEmailInviteFilters: (state, { payload }) => {
      state.emailInvite.filters = { ...emailInvite.filters };
    },
    clearEmailInviteFilter: (state, { payload }) => {
      const value = emailInviteHistoryFilters[payload];
      const filterKey = payload;
      
      state.emailInvite.filters = {
        ...state.emailInvite.filters,
        [filterKey]: value
      };
    },
    updatePrefillCtrlValue: (state, { payload }) => {
      const prefilledUrl = new URL(state.formUrl);
      const { displayId, value } = payload;

      state.prefillControls.map((ctrl) => {
        if (ctrl.displayId === displayId) {
          if (['country', 'text'].includes(ctrl.inputType)) ctrl.prefillValue = value.toString();
          else {
            if (typeof value === 'string') {
              const optionIndex = ctrl.options.findIndex((option) => option.value === value);
              ctrl.prefillValue = optionIndex;
            }

            if (Array.isArray(value)) {
              ctrl.prefillValue = ctrl.options.reduce((indexes, option, optionIndex) => {
                if (value.includes(option.value)) indexes.push(optionIndex);
                return indexes;
              }, []);
            }
          }

          ctrl.value = value;
        }
        
        if (ctrl.prefillValue !== undefined) {
          prefilledUrl.searchParams.append(ctrl.displayId, encodeURIComponent(ctrl.prefillValue));
        }
      });

      state.prefilledUrl = decodeURI(prefilledUrl.toString());
    },
    resetEmailInviteState: (state, { payload }) => {
      const { emailInvitePrefillCtrls } = getPrefillCtrls(state.form.definition.pages);
      state.emailInvite = {
        ...emailInvite,
        prefillControls: emailInvitePrefillCtrls
      };
    },
    setSavingFormOptions: (state, { payload }) => {
      state.isSavingForm = payload;
    },
    setActiveSidebarTab: (state, { payload }) => {
      state.activeSidebarTab = payload;
    },
    toggleMobileSidebar: (state, { payload }) => {
      state.showMobileSidebar = payload;
    },
    updateEmailInvite: (state, { payload }) => {
      state.emailInvite = { ...state.emailInvite, ...payload };
    },
    toggleActiveModal: (state, { payload }) => {
      state.activeModal = payload;
    },
    setUserOptions: (state, { payload }) => {
      state.userOptions = payload;
    },
    setReviewScale: (state, { payload }) => {
      state.reviewScale = payload;
    },
    setCustomUrl: (state, { payload }) => {
      state.customUrl = payload;
    },
    setShortUrl: (state, { payload }) => {
      const { isCustomUrl, shortUrl } = payload;
      state.generatedCustomUrl = isCustomUrl ? shortUrl : '';
      state.shortUrl = shortUrl;
      state.customUrl = '';
    },
    updateForm: (state, { payload }) => {
      const { isFormOption, prop, value } = payload;
      if (isFormOption) {
        state.form.options[prop] = value;
      } else {
        state.form[prop] = value;
      }
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
  },
  extraReducers: (share) => {
    share.addCase(fetchForm.fulfilled, (state, { payload }) => {
      state.isFetchingForm = false;

      if (payload.definition && !payload.error) {
        const form = formAmendForm(payload);

        const { emailInvitePrefillCtrls, prefillCtrls } = getPrefillCtrls(form.definition.pages);
        state.emailInvite.prefillControls = emailInvitePrefillCtrls;
        state.prefilledUrl = `${location.origin}/form/${form.id}`;
        state.emailInvite.testEmail = form.default_user_email;
        state.formUrl = `${location.origin}/form/${form.id}`;
        state.userCustomTexts = form.user_custom_texts;
        state.shortUrl = form.short_url ?? '';
        state.prefillControls = prefillCtrls;
        state.shareForm = form;
        state.form = form;
      }
      
      if (payload.error) {
        const isAccessDenied = Boolean(payload.status === '403' && payload.error.includes('form is inactive'));
        let errorMessage = payload.error;

        if (!isAccessDenied) {
          const errorType = payload.status === 404 ? 'notFound' : 'unknown';
          errorMessage = errorMessages.fetchForm[errorType];
          toast.error(errorMessage);
        } else {
          state.activeModal = 'accessDeniedModal';
        }

        console.error('Share Reducer:', errorMessage);
        state.error = errorMessage;

        // Log the error from the endpoint to Google Cloud.
        logErrorToCloudReporting(`Share Reducer Fetch Form Fulfilled: ${errorMessage} --> ${payload.error}`);
      }
    });
    share.addCase(fetchForm.rejected, (state, { payload }) => {
      console.error('Share Reducer:', payload);
      state.isFetchingForm = false;
      state.error = payload;
    });
    share.addCase(fetchForm.pending, (state) => {
      state.isFetchingForm = true;
    });
  }
});

export const {
  clearEmailInviteFilters,
  clearEmailInviteFilter,
  updatePrefillCtrlValue,
  resetEmailInviteState,
  setSavingFormOptions,
  setActiveSidebarTab,
  toggleMobileSidebar,
  updateEmailInvite,
  toggleActiveModal,
  setUserOptions,
  setReviewScale,
  setCustomUrl,
  setShortUrl,
  updateForm,
  setError
} = shareSlice.actions;

export default shareSlice.reducer;